import { useRef, useState, useCallback, useEffect } from 'react';
import { Auth } from 'aws-amplify';

// WebSocket connection states
export const WS_STATES = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
  RECONNECTING: 'reconnecting'
};

// Message types for better organization
export const MESSAGE_TYPES = {
  // Outgoing message types (Frontend -> Backend)
  LIVE_AUDIO_STREAM: 'live_audio_stream',
  AUDIO_CHUNK_LIVE: 'audio_chunk_live', // Legacy support
  START_INTERVIEW: 'start_interview', // Includes: interviewType ('screening' = 20 min, 'jd_specific' = 30 min)
  SCREEN_RECORDING_COMPLETE: 'screen_recording_complete',
  CONCLUSION_PLAYED: 'conclusion_played',
  PONG: 'pong',
  
  // Media Server message types (Frontend -> Media Server)
  MEDIA_SERVER_RECORDING_COMPLETE: 'media_server_recording_complete',
  
  // Incoming message types (Backend -> Frontend)
  SYNTHESIZED_SPEECH: 'synthesized_speech',
  STREAMING_INTERIM_TRANSCRIPT: 'streaming_interim_transcript',
  FINAL_CONSOLIDATED_TRANSCRIPT: 'final_consolidated_transcript', // Backend signal: Speech ended, close mic
  ENHANCED_STT_ERROR: 'enhanced_stt_error',
  LIVE_CAPTION: 'live_caption', // Legacy support
  TRANSCRIPT_FINAL: 'transcript_final', // Legacy support
  PING: 'ping',
  CONCLUSION_MESSAGE: 'conclusion_message',
  
  // Media Server message types (Media Server -> Frontend via Backend)
  MEDIA_SERVER_RECORDING_URL: 'media_server_recording_url',
  INTERVIEW_COMPLETED: 'interview_completed',
  VIDEO_URL_RECEIVED: 'video_url_received',
  EVALUATION_COMPLETE: 'evaluation_complete'
};

/**
 * Custom hook for managing WebSocket connection and message handling
 * @param {Object} config - Configuration object
 * @param {string} config.url - WebSocket URL
 * @param {function} config.onMessage - Message handler function
 * @param {boolean} config.autoConnect - Whether to auto-connect on mount
 * @param {number} config.reconnectAttempts - Max reconnection attempts
 * @param {number} config.reconnectDelay - Delay between reconnection attempts
 */
export const useWebSocket = ({
  url = 'wss://phone.getscreened.in/getscreenedbot/ws',
  onMessage,
  autoConnect = false,
  reconnectAttempts = 3,
  reconnectDelay = 1000
}) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const messageQueueRef = useRef([]);
  
  const [connectionState, setConnectionState] = useState(WS_STATES.DISCONNECTED);
  const [lastError, setLastError] = useState(null);

  // Clear reconnection timeout
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Send queued messages when connection is established
  const sendQueuedMessages = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && messageQueueRef.current.length > 0) {
      console.log(`📤 Sending ${messageQueueRef.current.length} queued messages`);
      messageQueueRef.current.forEach(message => {
        try {
          wsRef.current.send(message);
        } catch (error) {
          console.error('❌ Failed to send queued message:', error);
        }
      });
      messageQueueRef.current = [];
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log('🔗 WebSocket already connected or connecting');
      return;
    }

    console.log(`🔗 Connecting to WebSocket: ${url}`);
    setConnectionState(WS_STATES.CONNECTING);
    setLastError(null);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = async () => {
        console.log('✅ WebSocket connected successfully');
        setConnectionState(WS_STATES.CONNECTED);
        reconnectCountRef.current = 0;
        clearReconnectTimeout();
        
        // Send ID token for authentication on port 8080 connections
        if (url.includes(':8080')) {
          try {
            console.log('🔐 Port 8080 detected - sending ID token for authentication');
            const session = await Auth.currentSession();
            const idToken = session.getIdToken().getJwtToken(); // ✅ Correct ID token
            
            const authMessage = {
              type: 'auth',
              token: idToken // This is an ID token with 'aud' claim
            };
            
            console.log('📤 Sending ID token authentication to WebSocket server');
            ws.send(JSON.stringify(authMessage));
          } catch (authError) {
            console.error('❌ Failed to authenticate with WebSocket server:', authError);
            setLastError(authError);
            // Don't close connection, let it continue - some servers might not require auth
          }
        }
        
        // Send any queued messages
        sendQueuedMessages();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          onMessage?.(data, event);
        } catch (error) {
          console.error('❌ Failed to parse WebSocket message:', error);
          console.log('Raw message:', event.data);
        }
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket connection closed:', event.code, event.reason);
        wsRef.current = null;
        
        if (event.code !== 1000 && reconnectCountRef.current < reconnectAttempts) {
          // Abnormal closure, attempt to reconnect
          setConnectionState(WS_STATES.RECONNECTING);
          reconnectCountRef.current++;
          
          console.log(`🔄 Attempting to reconnect (${reconnectCountRef.current}/${reconnectAttempts}) in ${reconnectDelay}ms`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay * reconnectCountRef.current); // Exponential backoff
        } else {
          setConnectionState(WS_STATES.DISCONNECTED);
          if (reconnectCountRef.current >= reconnectAttempts) {
            setLastError(new Error(`Failed to reconnect after ${reconnectAttempts} attempts`));
          }
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setLastError(error);
        setConnectionState(WS_STATES.ERROR);
      };

    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
      setLastError(error);
      setConnectionState(WS_STATES.ERROR);
    }
  }, [url, onMessage, reconnectAttempts, reconnectDelay, sendQueuedMessages, clearReconnectTimeout]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log('🔌 Disconnecting WebSocket');
    clearReconnectTimeout();
    
    if (wsRef.current) {
      // Close with normal closure code
      wsRef.current.close(1000, 'User initiated disconnect');
      wsRef.current = null;
    }
    
    setConnectionState(WS_STATES.DISCONNECTED);
    reconnectCountRef.current = 0;
    messageQueueRef.current = [];
  }, [clearReconnectTimeout]);

  // Send message with automatic queuing if not connected
  const sendMessage = useCallback((message, queueIfDisconnected = true) => {
    const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(messageStr);
        return true;
      } catch (error) {
        console.error('❌ Failed to send WebSocket message:', error);
        return false;
      }
    } else if (queueIfDisconnected) {
      console.log('📝 Queuing message for when WebSocket connects');
      messageQueueRef.current.push(messageStr);
      return false; // Message queued, not sent immediately
    } else {
      console.warn('⚠️ WebSocket not connected and queuing disabled');
      return false;
    }
  }, []);

  // Send message with retry logic
  const sendMessageWithRetry = useCallback(async (message, maxRetries = 3, retryDelay = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const success = sendMessage(message, false);
      
      if (success) {
        return true;
      }
      
      if (attempt < maxRetries) {
        console.log(`🔄 Retry ${attempt}/${maxRetries} in ${retryDelay}ms`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
    
    console.error(`❌ Failed to send message after ${maxRetries} attempts`);
    return false;
  }, [sendMessage]);

  // Get current connection status
  const isConnected = connectionState === WS_STATES.CONNECTED;
  const isConnecting = connectionState === WS_STATES.CONNECTING;
  const isReconnecting = connectionState === WS_STATES.RECONNECTING;

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  return {
    // Connection management
    connect,
    disconnect,
    
    // Message sending
    sendMessage,
    sendMessageWithRetry,
    
    // Connection state
    connectionState,
    isConnected,
    isConnecting,
    isReconnecting,
    lastError,
    
    // WebSocket reference (for direct access if needed)
    wsRef: wsRef.current,
    
    // Queue status
    queuedMessageCount: messageQueueRef.current.length
  };
};

export default useWebSocket;
